import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { createCategory, updateCategory } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

export default function CategoryFormDialog({ open, category, onClose, onSaved }) {
  const isEdit = !!category?.id;
  const [descricao, setDescricao] = useState('');
  const [percentualComissao, setPercentualComissao] = useState('0');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setDescricao(category?.descricao || '');
    setPercentualComissao(String(category?.percentualComissao ?? 0));
    setTouched(false);
    setSaving(false);
    setError('');
  }, [open, category]);

  const descricaoInvalida = !descricao.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (descricaoInvalida) return;

    setSaving(true);
    setError('');
    try {
      const payload = {
        descricao: descricao.trim(),
        percentualComissao: Number(String(percentualComissao || 0).replace(',', '.')),
      };
      if (isEdit) {
        await updateCategory(category.id, { id: category.id, ...payload });
        onSaved?.('Categoria atualizada', { id: category.id, ...payload });
      } else {
        const { data } = await createCategory(payload);
        onSaved?.('Categoria criada', data);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao salvar a categoria.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ component: 'form', onSubmit: handleSubmit }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} noWrap>
            {isEdit ? 'Editar categoria' : 'Nova categoria'}
          </Typography>
          {isEdit && (
            <Typography variant="caption" color="text.secondary">
              Código {category.id}
            </Typography>
          )}
        </Stack>
        <IconButton onClick={onClose} disabled={saving} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Descrição"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            onBlur={() => setTouched(true)}
            error={touched && descricaoInvalida}
            helperText={touched && descricaoInvalida ? 'Informe a descrição.' : ' '}
            autoFocus
            fullWidth
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            label="% Comissão"
            type="number"
            value={percentualComissao}
            onChange={(e) => setPercentualComissao(e.target.value)}
            fullWidth
            inputProps={{ min: 0, max: 100, step: '0.01' }}
            helperText="Usado quando a empresa calcula comissão por categoria"
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isEdit ? 'Salvar' : 'Criar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
